import { useAuthStore } from "../slices/authStore";
import { useCallback } from "react";
import { ApiException } from "@/services/api";
import type {
  LoginCredentials,
  RegisterData,
} from "@/services/api/authService";
import { toast } from "sonner";
import i18n from "i18next";
import {
  UNSUPPORTED_ROLE_CODE,
  UNSUPPORTED_ROLE_MESSAGE,
} from "@/services/api/authMappers";
import {
  useLogin as useLoginMutation,
  useRegister as useRegisterMutation,
  useLogout as useLogoutMutation,
} from "@/features/auth/hooks/useAuthQueries";
import { UserRole } from "@/services/types/common.types";

/**
 * Custom hook for authentication.
 * State from Zustand (user, isAuthenticated, error, isInitializing).
 * API calls via TanStack Query (login, register, logout, getCurrentUser).
 * Derived helpers: hasRole, isAdmin, isTeacher, isStudent.
 */
export const useAuth = () => {
  const {
    user,
    isAuthenticated,
    isLoading: storeLoading,
    isInitializing,
    error,
    setError,
    updateUser,
    initializeAuth,
    hasRole,
  } = useAuthStore();

  const loginMutation = useLoginMutation();
  const registerMutation = useRegisterMutation();
  const logoutMutation = useLogoutMutation();

  const isLoading =
    isInitializing ||
    storeLoading ||
    loginMutation.isPending ||
    registerMutation.isPending ||
    logoutMutation.isPending;

  const handleLogin = useCallback(
    async (credentials: LoginCredentials) => {
      setError(null);
      try {
        const result = await loginMutation.mutateAsync(credentials);
        return result.data;
      } catch (err) {
        const errorMessage =
          err instanceof ApiException && err.code === UNSUPPORTED_ROLE_CODE
            ? (i18n.t("common:toast.unsupportedRole") || UNSUPPORTED_ROLE_MESSAGE)
            : err instanceof ApiException
              ? err.message
              : "Login failed. Please try again.";
        setError(errorMessage);
        toast.error(errorMessage);
        throw err;
      }
    },
    [loginMutation, setError],
  );

  const handleRegister = useCallback(
    async (data: RegisterData) => {
      setError(null);
      try {
        const result = await registerMutation.mutateAsync(data);
        toast.success("Registration successful!");
        return result.data;
      } catch (err) {
        const errorMessage =
          err instanceof ApiException
            ? err.message
            : "Registration failed. Please try again.";
        setError(errorMessage);
        toast.error(errorMessage);
        throw err;
      }
    },
    [registerMutation, setError],
  );

  const handleLogout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync();
      toast.success("Logged out successfully");
    } catch (err) {
      useAuthStore.getState().logout();
      toast.success("Logged out successfully");
      console.error("Logout error:", err);
    }
  }, [logoutMutation]);

  return {
    user,
    profile: user?.profile,
    isAuthenticated,
    isLoading,
    error,

    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    updateUser,
    initializeAuth,

    hasRole,
    isAdmin: hasRole(UserRole.ADMIN),
    isTeacher: hasRole(UserRole.TEACHER),
    isStudent: hasRole(UserRole.STUDENT),
    isCreator: hasRole(UserRole.CREATOR),
  };
};
